import { useWebchatActions } from '@/hooks/use-webchat-actions/use-webchat-actions';
import { useWebchatStore } from '@/store/webchat.store';
import { getAvailableAgents } from '@/services/webchat-service/webchat.service';
import { AvailableAgent, getVisitorLabel, isAiCallSession } from '@/services/webchat-service/webchat.service.types';
import { useAiCallBridge } from '@/hooks/use-ai-call-bridge/use-ai-call-bridge';
import { ReactQueryKeys } from '@/utils/react-query-keys';
import { useQuery } from '@tanstack/react-query';
import { Button, Descriptions, Input, Modal, Select, Space, Tabs, Tag } from 'antd';
import { AudioMutedOutlined, AudioOutlined, PhoneOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { ConferenceCallSection } from '../conference-call-section/conference-call-section';
import { UserProfilePanel } from '../user-profile-panel/user-profile-panel';
import styles from './actions-sidebar.module.scss';

interface ActionsSidebarProps {
  onSetAgentLanguage?: (conversationId: number, language: string) => void;
  onToggleTranslate?: (conversationId: number, translate: boolean) => void;
}

export const ActionsSidebar = ({ onSetAgentLanguage, onToggleTranslate }: ActionsSidebarProps) => {
  const { t } = useTranslation(['webchat', 'common']);
  // Per-field selectors — see WebchatHeader for the full rationale. The
  // sidebar previously subscribed to the entire store and re-rendered
  // on every WebSocket-driven mutation of `sessions`. Reading the
  // session object via a derived selector means the sidebar only
  // re-renders when *this caller's* fields change.
  const selectedConversationId = useWebchatStore((s) => s.selectedConversationId);
  const session = useWebchatStore((s) =>
    s.selectedConversationId == null
      ? undefined
      : s.sessions.find((c) => c.id === s.selectedConversationId)
  );
  const bridgeWarnings = useWebchatStore((s) => s.bridgeWarnings);
  const translateEnabled = useWebchatStore((s) => s.translateEnabled);
  const targetLanguage = useWebchatStore((s) => s.targetLanguage);
  const { claim, close, transfer, saveNotes, isClaiming, isClosing, isTransferring } = useWebchatActions();
  const aiBridge = useAiCallBridge(selectedConversationId ?? undefined);
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [selectedAgentId, setSelectedAgentId] = useState<number | null>(null);
  const [transferNotes, setTransferNotes] = useState('');
  const [notesValue, setNotesValue] = useState('');

  const isAiCall = isAiCallSession(session);
  const isBridgeActive = isAiCall && !!session?.assigned_agent_id && aiBridge.status !== 'idle';
  const bridgeWarning = selectedConversationId ? bridgeWarnings[selectedConversationId] : undefined;

  useEffect(() => {
    setNotesValue(session?.notes || '');
  }, [session?.id, session?.notes]);

  const { data: agents } = useQuery<AvailableAgent[]>({
    queryKey: ReactQueryKeys.WEBCHAT_AVAILABLE_AGENTS(),
    queryFn: getAvailableAgents,
    enabled: transferModalOpen,
  });

  // Keep the right column mounted at its full width regardless of
  // selection so the middle ChatPanel doesn't expand-then-shrink every
  // time a card is clicked. The earlier "return null when no session"
  // optimisation produced a visible layout shift on every selection
  // change, which read as the whole workspace "resizing" in the
  // recorded demo. A muted placeholder fills the slot until a session
  // is chosen.
  if (!session) {
    return (
      <div className={styles.sidebar}>
        <div className={styles.emptyState}>{t('webchat:actionsSidebar.emptyState')}</div>
      </div>
    );
  }

  const handleClaim = async () => {
    try {
      const result = await claim({ id: session.id, translate: translateEnabled, agentLanguage: targetLanguage });
      if (result?.mode === 'translated_bridge' && result.agent_language) {
        // Pass the STI subclass so the bridge service uses the right
        // TwiML swap path for AnalyticsCall (rai handoff) vs.
        // ElevenLabsCall (ConvAI handoff). Both go through the same
        // translation bridge endpoint, but the conversation_type
        // parameter drives recording + announcement semantics.
        const conversationType =
          session.type === 'AnalyticsCall' ? 'AnalyticsCall' : 'ElevenLabsCall';
        await aiBridge.connect(
          result.agent_language,
          result.visitor_language || session.language || 'en',
          translateEnabled,
          conversationType,
        );
        // Ensure the LLM bridge uses the dropdown's target language, which may
        // differ from the agent's stored preferred_language used at claim time.
        if (translateEnabled && onSetAgentLanguage) {
          onSetAgentLanguage(session.id, targetLanguage);
        }
      }
    } catch {
      // Claim error is handled by React Query's onError — don't connect bridge
    }
  };

  const handleClose = () => {
    Modal.confirm({
      title: t('webchat:actionsSidebar.closeConfirm.title'),
      content: isBridgeActive
        ? t('webchat:actionsSidebar.closeConfirm.contentBridge')
        : t('webchat:actionsSidebar.closeConfirm.content'),
      okText: t('common:actions.close'),
      cancelText: t('common:actions.cancel'),
      okButtonProps: { danger: true },
      onOk: async () => {
        // Tear down the local Twilio device first so the bridge stream
        // ends cleanly; the server-side close (HangupTwilioLegJob) hangs
        // up the caller leg afterward.
        if (isBridgeActive) {
          aiBridge.disconnect();
        }
        await close({ id: session.id });
      },
    });
  };

  const handleTransfer = async () => {
    if (!selectedAgentId) return;
    await transfer({ id: session.id, agentId: selectedAgentId, notes: transferNotes || undefined });
    setTransferModalOpen(false);
    setSelectedAgentId(null);
    setTransferNotes('');
  };

  // Info tab body — extracted so each tab can render its slice without an
  // umbrella div fighting Antd Tabs' own padding.
  const infoTab = (
    <>
      <Descriptions column={1} size="small" title={t('webchat:actionsSidebar.infoTitle')}>
        <Descriptions.Item label={t('webchat:fields.id')}>#{session.id}</Descriptions.Item>
        <Descriptions.Item label={t('webchat:fields.name')}>
          {getVisitorLabel(session)}
        </Descriptions.Item>
        <Descriptions.Item label={t('webchat:fields.phone')}>
          {session.visitor_phone || t('common:states.none')}
        </Descriptions.Item>
        <Descriptions.Item label={t('webchat:fields.language')}>{session.language}</Descriptions.Item>
        <Descriptions.Item label={t('webchat:fields.status')}>
          {t(`webchat:status.${session.webchat_status}`)}
        </Descriptions.Item>
        <Descriptions.Item label={t('webchat:fields.agent')}>
          {session.assigned_agent_name || t('webchat:fields.unassigned')}
        </Descriptions.Item>
        <Descriptions.Item label={t('webchat:fields.created')}>
          {dayjs(session.created_at).format('D MMM, HH:mm')}
        </Descriptions.Item>
      </Descriptions>

      <div className={styles.actions}>
        {(session.webchat_status === 'queued' || session.webchat_status === 'bot') && (
          <Button type="primary" onClick={handleClaim} loading={isClaiming} block>
            {t('webchat:actionsSidebar.claim')}
          </Button>
        )}
        {isAiCall && session.webchat_status === 'live' && !session.assigned_agent_id && (
          <Button type="primary" onClick={handleClaim} loading={isClaiming} block icon={<PhoneOutlined />}>
            {t('webchat:actionsSidebar.claimAiCall')}
          </Button>
        )}
        {isAiCall && session.webchat_status === 'claiming' && (
          <Button type="primary" loading block icon={<PhoneOutlined />} disabled>
            {t('webchat:actionsSidebar.connecting')}
          </Button>
        )}
        {session.webchat_status === 'live' && !isAiCall && (
          <Button onClick={() => setTransferModalOpen(true)} loading={isTransferring} block>
            {t('webchat:actionsSidebar.reassign')}
          </Button>
        )}
        {session.webchat_status !== 'closed' && (
          <Button danger onClick={handleClose} loading={isClosing} block>
            {t('webchat:actionsSidebar.close')}
          </Button>
        )}
      </div>

      {bridgeWarning?.kind === 'voice_rejected' && (
        // Surfaced from participant_handler._rebuild_tts_with_default_voice.
        // The configured voice was rejected by ElevenLabs (1008 / not_found
        // or inline error) so the bridge fell back to a language-default
        // voice. The rep is hearing the wrong voice; flag the agent config
        // as the place to fix it.
        <Tag color="orange" style={{ marginBottom: 8, padding: '4px 8px', whiteSpace: 'normal', display: 'block' }}>
          <Trans
            ns="webchat"
            i18nKey="actionsSidebar.voiceRejected"
            values={{
              role: t(`webchat:actionsSidebar.voiceRejectedRole.${bridgeWarning.role}`),
              language: String(
                bridgeWarning.details?.language ?? t('webchat:actionsSidebar.voiceRejectedUnknownLanguage')
              ),
            }}
            components={{ strong: <strong /> }}
          />
        </Tag>
      )}

      {isBridgeActive && (
        <div className={styles.actions}>
          <Tag color="green" style={{ width: '100%', textAlign: 'center', padding: '4px 0' }}>
            {t(
              translateEnabled
                ? 'webchat:actionsSidebar.bridgeTag.translating'
                : 'webchat:actionsSidebar.bridgeTag.passthrough',
              { status: t(`webchat:deviceStatus.${aiBridge.status}`) }
            )}
          </Tag>
          <Space wrap>
            <Button
              icon={aiBridge.isMuted ? <AudioMutedOutlined /> : <AudioOutlined />}
              onClick={aiBridge.toggleMute}
              size="small"
            >
              {aiBridge.isMuted ? t('webchat:mic.unmute') : t('webchat:mic.mute')}
            </Button>
            <Button
              size="small"
              type={translateEnabled ? 'default' : 'primary'}
              onClick={() => {
                const next = !translateEnabled;
                onToggleTranslate?.(session.id, next);
                if (next && onSetAgentLanguage) {
                  onSetAgentLanguage(session.id, targetLanguage);
                }
              }}
            >
              {translateEnabled
                ? t('webchat:actionsSidebar.translation.disable')
                : t('webchat:actionsSidebar.translation.enable')}
            </Button>
            {/* No standalone "End Bridge" — "Close Conversation" below now
                tears down the bridge first, then closes the call. Keeping
                two parallel close paths confused admins. */}
            {(aiBridge.status === 'idle' || aiBridge.status === 'error') && session.assigned_agent_id && (
              <Button icon={<PhoneOutlined />} onClick={aiBridge.reconnect} size="small">
                {t('webchat:actionsSidebar.reconnect')}
              </Button>
            )}
          </Space>
          {aiBridge.error && (
            <Tag color="red" style={{ marginTop: 4 }}>{aiBridge.error}</Tag>
          )}
        </div>
      )}

      {session.webchat_status === 'live' && !isAiCall && (
        <ConferenceCallSection key={session.id} conversationId={session.id} companyId={session.company_id} visitorLanguage={session.language} />
      )}
    </>
  );

  const notesTab = (
    <div className={styles.notesSection}>
      <div className={styles.notesLabel}>{t('webchat:actionsSidebar.notes.label')}</div>
      <Input.TextArea
        rows={6}
        placeholder={t('webchat:actionsSidebar.notes.placeholder')}
        value={notesValue}
        onChange={(e) => setNotesValue(e.target.value)}
        onBlur={() => {
          if (session && notesValue !== (session.notes || '')) {
            saveNotes({ id: session.id, notes: notesValue });
          }
        }}
      />
    </div>
  );

  // Profile tab is added only when the conversation has been linked to a User
  // record (via FindOrCreateUserService at call/SMS init). Anonymous callers
  // — those without a phone match — see only Info + Notes, since there's
  // nothing useful to show without an account.
  const tabItems = [
    { key: 'info', label: t('webchat:actionsSidebar.tabs.info'), children: infoTab },
    ...(session.user_id
      ? [
          {
            key: 'profile',
            label: t('webchat:actionsSidebar.tabs.profile'),
            children: <UserProfilePanel userId={session.user_id} />,
          },
        ]
      : []),
    { key: 'notes', label: t('webchat:actionsSidebar.tabs.notes'), children: notesTab },
  ];

  return (
    <div className={styles.sidebar}>
      <Tabs defaultActiveKey="info" items={tabItems} />

      <Modal
        title={t('webchat:actionsSidebar.reassign')}
        open={transferModalOpen}
        onCancel={() => {
          setTransferModalOpen(false);
          setSelectedAgentId(null);
          setTransferNotes('');
        }}
        onOk={handleTransfer}
        okText={t('webchat:actionsSidebar.transfer.ok')}
        cancelText={t('common:actions.cancel')}
        okButtonProps={{ disabled: !selectedAgentId }}
      >
        <Select
          style={{ width: '100%', marginBottom: 12 }}
          placeholder={t('webchat:actionsSidebar.transfer.selectPlaceholder')}
          value={selectedAgentId}
          onChange={setSelectedAgentId}
          options={[...(agents || [])]
            .sort((a, b) => (a.webchat_available === b.webchat_available ? 0 : a.webchat_available ? -1 : 1))
            .map((a) => ({
              value: a.id,
              label: `${a.webchat_available ? '\u{1F7E2}' : '\u{26AA}'} ${
                a.webchat_available
                  ? t('webchat:actionsSidebar.transfer.agentOption', { name: a.name, count: a.active_count })
                  : t('webchat:actionsSidebar.transfer.agentOptionUnavailable', {
                      name: a.name,
                      count: a.active_count,
                    })
              }`,
              disabled: !a.webchat_available,
            }))}
        />
        <Input.TextArea
          rows={2}
          placeholder={t('webchat:actionsSidebar.transfer.notesPlaceholder')}
          value={transferNotes}
          onChange={(e) => setTransferNotes(e.target.value)}
        />
      </Modal>
    </div>
  );
};
